import { CreateOrg, UpdateOrg } from "@humanex/interfaces";
import { toast, useI18n } from "@humanex/ui/libs";
import { useForm } from "@tanstack/react-form";
import { createFileRoute, useNavigate, useRouteContext } from "@tanstack/react-router";

import { useUpdateOrg } from "@/hooks/queries/use-org-query";

import { OrgForm } from "../../create/-components/org-form";

export const Route = createFileRoute("/_authenticated/org/$orgId/edit/")({
  component: RouteComponent
});

function RouteComponent() {
  const { t } = useI18n();
  const { org } = useRouteContext({ from: "/_authenticated/org/$orgId/edit/" });

  const navigate = useNavigate();

  const updateOrgMutation = useUpdateOrg({
    onSuccess: () => {
      toast.success(t("org.edit.success"));
      navigate({ to: "/org/$orgId", params: { orgId: org.id } });
    }
  });

  const form = useForm<CreateOrg>({
    defaultValues: {
      name: org.name,
      tagline: org.tagline,
      description: org.description,
      mission: org.mission,
      avatar: org.avatar,
      cover: org.cover,
      contact: {
        email: org.contact?.email || null,
        phone: org.contact?.phone || null,
        website: org.contact?.website || null,
        instagram: org.contact?.instagram || null,
        facebook: org.contact?.facebook || null,
        twitter: org.contact?.twitter || null,
        youtube: org.contact?.youtube || null
      }
    },
    onSubmit: async ({ value }) => {
      // Clean up the data - remove null/empty values
      const updateData: UpdateOrg = {};

      if (value.name && value.name.trim() !== org.name) {
        updateData.name = value.name.trim();
      }
      if (value.tagline && value.tagline.trim() !== org.tagline) {
        updateData.tagline = value.tagline.trim();
      }
      if (value.description && value.description.trim() !== org.description) {
        updateData.description = value.description.trim();
      }
      if (value.mission && value.mission.trim() !== org.mission) {
        updateData.mission = value.mission.trim();
      }
      if (value.avatar && value.avatar.trim() !== org.avatar) {
        updateData.avatar = value.avatar.trim();
      }
      if (value.cover && value.cover.trim() !== org.cover) {
        updateData.cover = value.cover.trim();
      }

      // Handle contact info
      if (value.contact) {
        const contactChanges: any = {};
        let hasContactChanges = false;

        if (value.contact.email !== org.contact?.email) {
          contactChanges.email = value.contact.email || null;
          hasContactChanges = true;
        }
        if (value.contact.phone !== org.contact?.phone) {
          contactChanges.phone = value.contact.phone || null;
          hasContactChanges = true;
        }
        if (value.contact.website !== org.contact?.website) {
          contactChanges.website = value.contact.website || null;
          hasContactChanges = true;
        }
        if (value.contact.instagram !== org.contact?.instagram) {
          contactChanges.instagram = value.contact.instagram || null;
          hasContactChanges = true;
        }
        if (value.contact.facebook !== org.contact?.facebook) {
          contactChanges.facebook = value.contact.facebook || null;
          hasContactChanges = true;
        }
        if (value.contact.twitter !== org.contact?.twitter) {
          contactChanges.twitter = value.contact.twitter || null;
          hasContactChanges = true;
        }
        if (value.contact.youtube !== org.contact?.youtube) {
          contactChanges.youtube = value.contact.youtube || null;
          hasContactChanges = true;
        }

        if (hasContactChanges) {
          updateData.contact = contactChanges;
        }
      }

      // Only update if there are changes
      if (Object.keys(updateData).length > 0) {
        updateOrgMutation.mutate({ orgId: org.id, data: updateData });
      } else {
        toast.info(t("org.edit.noChanges"));
      }
    }
  });

  const onCancel = () => {
    navigate({ to: "/org/$orgId", params: { orgId: org.id } });
  };

  return (
    <div className="py-4 md:py-6 lg:py-8 px-0 sm:px-2 md:px-4 lg:px-8 space-y-6">
      <div className="space-y-4 sm:space-y-6 max-w-4xl mx-auto">
        <div className="px-4 md:px-6">
          <p className="text-responsive-xl font-normal text-text-primary mb-2">{t("org.edit.title")}</p>
          <p className="text-responsive-base text-text-secondary">
            {t("org.edit.subtitle", { orgName: org.name })}
          </p>
        </div>

        <OrgForm form={form} isSubmitting={updateOrgMutation.isPending} onCancel={onCancel} orgId={org.id} />
      </div>
    </div>
  );
}
